/* eslint-disable no-console */
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import {Button, Steps, Drawer, Flex, Form} from 'antd';
import React, {useEffect, useState} from 'react';

import {detectAutoDetect, drawerZIndex} from '../../../constants/Common';
import {statusEm} from '../../../pages/netZero/utils/commonFunctions';
import {APIType, APITypeEnum} from '../../../services/interface/tableAPIType';
import activeFacilityCount from '../../../services/modules/OM/activeFacilityCount';
import subscriptionDetails from '../../../services/modules/OM/subscriptionDetails';
import getTotalUserCount from '../../../services/modules/User/totalUserCount';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../../utils/commonFunctions';
import showMessage from '../../../utils/showMessage';
import Loader from '../../Loader/Loader';
import processTimeFrameData from '../../MonthlyYearly/Utils/processAllTimeFrame';
import {makeCheckKeyLabel} from '../../Table/Constants/Common';
import {getFromAndTo, getFromAndToInData} from '../../Table/Utils/getFromAndTo';

import DropdownConfigProvider from './DropdownConfigProvider';
import FormPreview from './FormPreview';
import MySvgComponent from './StepIconDrawer';

interface FromComponentProps {
  currentStep?: number;
  onStepsChange?: (steps: string[]) => void;
  props?: any;
}

interface ModalFormProps {
  open: boolean;
  onCancel: () => void;
  FormComponent: React.ReactElement;
  title?: string;
  initialValue?: any;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  API?: (
    type: APIType,
    shouldCall?: boolean | any,
    data?: any,
    id?: string
  ) => Promise<any>;
  data?: any;
  idKey?: string;
  buttonText?: string;
  table?: boolean;
  type?: {key: string; value: string} | undefined;
  buttonDisabled?: {
    value: boolean;
    setter: React.Dispatch<React.SetStateAction<boolean>>;
  };
  fullScreen?: boolean;
  onlyYear?: boolean;
  isSupplierInfo?: boolean;
  formPreview?: any[];
  stepsTitle: any[];
  isChecker?: boolean;
  isConfigurator?: boolean;
  isAction?: boolean;
  neglectReset?: boolean;
  UMData?: any;
}

const DrawerForm: React.FC<ModalFormProps> = ({
  open,
  onCancel,
  API,
  initialValue,
  data = undefined,
  FormComponent,
  setRefresh,
  buttonText = 'Finish',
  idKey = '',
  table = false,
  type = undefined,
  buttonDisabled = null,
  onlyYear = false,
  isSupplierInfo = false,
  formPreview,
  stepsTitle,
  isChecker = false,
  isConfigurator = false,
  isAction = true,
  neglectReset = false,
  UMData = {}
}) => {
  const [form] = Form.useForm();
  const {getFieldsValue} = form;
  const [currentStep, setCurrentStep] = useState(0);
  const companyId = useCompanyStore.getState().companyId;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(isChecker);

  const [steps, setSteps] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setSteps(stepsTitle);
    }
  }, [stepsTitle, open]);

  const userDetails = useUserStore.getState().userDetails;

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setIsConfirmed(false);
    }
  }, [open]);

  useEffect(() => {
    const lastIndex = Math.max(0, steps.length - 1);
    if (currentStep > lastIndex) {
      setCurrentStep(lastIndex);
    }
  }, [steps.length, currentStep]);

  useEffect(() => {
    if (!neglectReset) {
      form.resetFields();
      if (data !== undefined) {
        form.setFieldsValue(data);
        setIsLoading(false);
        setIsSubmitDisabled(true);
      } else {
        form.resetFields(initialValue);
        setIsLoading(false);
        setIsSubmitDisabled(true);
      }
    } else {
      setIsLoading(false);
      setIsSubmitDisabled(false);
    }
  }, [open, data]);

  const {Step} = Steps;

  const isLast = steps.length - 1;

  const next = () => {
    form
      .validateFields()

      .then(() => {
        setCurrentStep(currentStep + 1);
      })
      .catch((errorInfo) => {
        if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
          form.scrollToField(errorInfo.errorFields[0].name);
        }
      });
  };

  const prev = () => {
    isConfirmed ? setIsConfirmed(false) : setCurrentStep(currentStep - 1);
  };

  const handleResponse = (res: any) => {
    if (res?.status === 'error' || res === undefined) {
      showMessage(res);
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(false);
      }
      if (res?.reloadSubscription) {
        if (
          res.reloadType === 'externalStakeholders' ||
          res.reloadType === 'internalUsers'
        ) {
          Promise.all([subscriptionDetails(), getTotalUserCount()])
            .then(() => {
              onCancel();
              setRefresh(true);
            })
            .catch(() => {});
        } else if (res.reloadType === 'facility') {
          Promise.all([subscriptionDetails(), activeFacilityCount()])
            .then(() => {
              onCancel();
              setRefresh(true);
            })
            .catch(() => {});
        }
      }
    } else if (res.status === 'success') {
      showMessage(res);
      onCancel();
      setRefresh(true);
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(true);
      }
    } else {
      setIsSubmitted(false);
      if (buttonDisabled) {
        buttonDisabled.setter(false);
      }
    }
  };

  const removeIdForUpdate = (value: any) => {
    const newValue = Object.keys(value).reduce((acc: any, key) => {
      if (
        key !== '_id' &&
        key !== '__v' &&
        key !== 'key' &&
        key !== 'fullAddress'
      ) {
        acc[key] = value[key] === undefined ? null : value[key];
      }

      return acc;
    }, {});

    return newValue;
  };

  const onSubmit = () => {
    const value = form.getFieldsValue(true);

    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null; // Replace with a suitable placeholder value
      }
    });

    if (isSupplierInfo) {
      value.company = companyId;
    }

    let ftData;

    if (value.year && value.facility) {
      ftData = getFromAndToInData(value.year, value.facility);
    } else {
      ftData = getFromAndTo(idKey);
    }

    const {year, fromDate, toDate, facilityId} = ftData;

    const mainData = processTimeFrameData(
      data?._id ? removeIdForUpdate(value) : value,
      fromDate,
      toDate
    );

    if (type) {
      mainData[type.key] = type.value;
    }

    if (data?._id) {
      mainData.updatedBy = userDetails._id;
    } else {
      mainData.createdBy = userDetails._id;
      mainData.updatedBy = userDetails._id;
    }

    if (API && !idKey && !table && !onlyYear && !neglectReset) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData,
          company: companyId
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {});
    } else if (API && table && facilityId && year && !onlyYear) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData,
          ...(hasUserRole(UserRole.Both_role) && !isConfigurator && isAction
            ? {
                [makeCheckKeyLabel.STATUS.key]: statusEm.ACCEPTED,
                [makeCheckKeyLabel.COMMENT.key]: ''
              }
            : {}),
          facility: facilityId,
          year
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {
          setIsSubmitted(false);
          if (buttonDisabled) {
            buttonDisabled.setter(false);
          }
        });
    } else if (API && table && year && onlyYear) {
      API(
        data?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
        {
          ...mainData,
          ...(hasUserRole(UserRole.Both_role) && !isConfigurator && isAction
            ? {
                [makeCheckKeyLabel.STATUS.key]: statusEm.ACCEPTED,
                [makeCheckKeyLabel.COMMENT.key]: ''
              }
            : {}),
          company: companyId,
          year: mainData?.year ?? year
        },
        data?._id ? data?._id : ''
      )
        .then((res) => {
          handleResponse(res);
        })
        .catch(() => {
          setIsSubmitted(false);
          if (buttonDisabled) {
            buttonDisabled.setter(false);
          }
        });
    } else {
      if (API) {
        API(APITypeEnum.GET_ALL, false, {}, '')
          .then((res) => {
            handleResponse(res);
          })
          .catch(() => {
            setIsSubmitted(false);
            if (buttonDisabled) {
              buttonDisabled.setter(false);
            }
          });
      }
    }
  };

  const onConfirmAndSave = () => {
    form.submit();
    setIsSubmitted(true);
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(() => {
        setIsConfirmed(true);
      })
      .catch(() => {});
  };

  const onStepClick = (index: number) => {
    if (currentStep < index || !isConfirmed) {
      form
        .validateFields()
        .then(() => {
          setCurrentStep(index);
        })
        .catch(() => {});
    } else {
      setCurrentStep(index);
      setIsConfirmed(false);
    }
  };

  const minTwoDigits = (n: number) => {
    return (n < 10 ? '0' : '') + n;
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Drawer
      placement={'right'}
      size="large"
      onClose={() => {
        onCancel();
        setIsSubmitted(false);
        setTimeout(() => {
          setCurrentStep(0);
        }, 1000);
      }}
      destroyOnClose={true}
      zIndex={drawerZIndex}
      footer={
        <Flex className="drawerButtonContainer">
          <div>
            <Button
              onClick={() => {
                onCancel();
                setIsSubmitted(false);
                setTimeout(() => {
                  setCurrentStep(0);
                }, 1000);
              }}
              className="drawerButton drawerCancel"
            >
              Cancel
              <CloseOutlined />
            </Button>
          </div>
          <div className="rightSideNextDone">
            {(currentStep > 0 || isConfirmed) && (
              <Button
                style={{margin: '0 8px'}}
                onClick={() => {
                  prev();
                }}
                className="drawerButton Back"
                disabled={isSubmitted}
              >
                <ArrowLeftOutlined />
                Back
              </Button>
            )}
            {!isConfirmed && currentStep < isLast && (
              <Button
                className="drawerButton NextDone"
                onClick={() => {
                  next();
                }}
              >
                Next
                <ArrowRightOutlined />
              </Button>
            )}
            {!isConfirmed && currentStep === isLast && !isChecker && (
              <Button
                className="drawerButton NextDone"
                onClick={() => {
                  onFinish();
                }}
                loading={isSubmitted}
                disabled={isSubmitDisabled}
              >
                {buttonText}
              </Button>
            )}
            {(isConfirmed || isChecker) && (
              <Button
                type="primary"
                className="drawerButton confirmSave"
                onClick={() => {
                  onConfirmAndSave();
                }}
                loading={isSubmitted}
                htmlType="submit"
              >
                {isChecker ? 'Save' : 'Confirm & Save'}
                <CheckOutlined />
              </Button>
            )}
          </div>
        </Flex>
      }
      open={open}
      closeIcon={false}
      title={
        <Flex className="stepsBox">
          <Steps current={currentStep}>
            {steps.map((step, index) => {
              const isFinished = currentStep > index || isConfirmed;
              const isProcess = currentStep === index;

              return (
                <Step
                  key={step}
                  title={
                    <div
                      style={{
                        whiteSpace: 'pre-line',
                        maxWidth: steps.length >= 3 ? '135px' : '180px'
                      }}
                    >
                      {step}
                    </div>
                  }
                  onClick={() => {
                    onStepClick(index);
                  }}
                  style={{
                    cursor: 'pointer',
                    textAlign: steps.length === 1 ? 'left' : 'center'
                  }}
                  icon={
                    <div
                      className={
                        isFinished
                          ? 'stepIconCircle isFinished'
                          : isProcess
                            ? 'stepIconCircle isProcess'
                            : 'stepIconCircle'
                      }
                    >
                      <MySvgComponent
                        number={minTwoDigits(index + 1)}
                        isFinished={isFinished}
                        isProcess={isProcess}
                      />
                    </div>
                  }
                />
              );
            })}
          </Steps>
        </Flex>
      }
      style={{background: '#F3F4F8'}}
    >
      <div className="bodyPage">
        <div style={{height: '100%'}}>
          {isConfirmed && !isChecker ? (
            <>
              <div className="stepTitle">{'Preview Details'}</div>
              <div className="formLayout formPreview">
                <FormPreview
                  formData={
                    neglectReset
                      ? {
                          ...UMData,
                          selectedPermissions:
                            getFieldsValue(true).selectedPermissions
                        }
                      : getFieldsValue(true)
                  }
                  formPreview={formPreview ?? []}
                />
              </div>
            </>
          ) : (
            <DropdownConfigProvider>
              <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                scrollToFirstError={{
                  behavior: 'smooth',
                  block: 'start',
                  scrollMode: 'if-needed'
                }}
                onFinishFailed={() => {
                  setIsSubmitted(false);
                }}
                onValuesChange={() => {
                  setIsSubmitDisabled(false);
                }}
                autoComplete={detectAutoDetect()}
                className="drawerForm"
              >
                <div className="formLayout">
                  {React.cloneElement(
                    FormComponent as React.ReactElement<FromComponentProps>,
                    {
                      currentStep,
                      ...FormComponent.props
                    }
                  )}
                </div>
              </Form>
            </DropdownConfigProvider>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerForm;
